import store from 'store2';
import status from './status';
import normalize from './config/normalize';
import { createModelInfo } from '../common/ModelInfoContext';

const buildUrl = (url, query) => {
  if (!query) {
    return url;
  }
  const searchParams = new URLSearchParams(query);
  searchParams.sort();
  return `${url}?${searchParams.toString()}`;
};

const buildAuth = (enabled, redirectUrl) => {
  return {
    enabled,
    redirectUrl,
    token: store.get('byo.auth') || '',
  };
};

const checkAuth = (auth) => (res) => {
  if (!auth.enabled) {
    return res;
  }
  if (res.status === 401) {
    store.remove('byo.auth');
    if (!TEST) {
      // avoid calling during tests since we can't mock out
      window.location.replace(auth.redirectUrl);
    }
  } else if (res.headers.get('x-byo-auth')) {
    store.set('byo.auth', res.headers.get('x-byo-auth'));
  }
  return res;
};

const fetchGet = (url, auth) => {
  const authHeaders = auth.enabled ? { 'x-byo-auth': auth.token } : {};
  const options = {
    headers: {
      ...authHeaders,
    },
  };
  return fetch(url, options)
    .then(checkAuth(auth))
    .then(status)
    .then((res) => res.json());
};

const fetchPost = (url, data, auth) => {
  const authHeaders = auth.enabled ? { 'x-byo-auth': auth.token } : {};
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...authHeaders,
    },
    body: JSON.stringify(data),
  };
  return fetch(url, options)
    .then(checkAuth(auth))
    .then(status)
    .then((res) => res.json());
};

export const fetchConfig = (options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    bodyStyle,
    carline,
    year,
    v3ServicesPath,
    enableAuth,
    authRedirectUrl,
  } = modelInfo;
  const { postalCode, styleId } = options;

  const url = `${v3ServicesPath}/fully-configured/${language}/${country}/${brand}/${carline}/${year}/${bodyStyle}/${postalCode}`;
  const query = styleId
    ? {
        styleId,
      }
    : null;

  const start = Date();
  return fetchGet(buildUrl(url, query), buildAuth(enableAuth, authRedirectUrl))
    .then(normalize(modelInfo))
    .then((config) => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      const duration = Date() - start;
      window?.heap.track('BYO-Retrieve-Configuration', {'load': 'success', 'duration': duration});
      return config;
    })
    .catch((error) => {
      const duration = Date() - start;
      window?.heap.track('BYO-Retrieve-Configuration', {'load': 'error', 'status': error, 'duration': duration});
    });
};

export const fetchByStyleIdAndSerializedState = (styleId, ss, options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    bodyStyle,
    carline,
    year,
    v3ServicesPath,
    enableAuth,
    authRedirectUrl,
  } = modelInfo;
  const { postalCode } = options;
  const key = `${styleId}-${ss}-${postalCode}`;
  if (cache.has(key)) {
    return Promise.resolve(cache.get(key));
  }
  const url = `${v3ServicesPath}/retrieve-configuration`;
  const start = Date();
  return fetchPost(
    buildUrl(url),
    {
      style: `${language}/${country}/${brand}/${carline}/${year}/${bodyStyle}/${styleId}`,
      ss: ss,
      postalCode: options.postalCode,
    },
    buildAuth(enableAuth, authRedirectUrl)
  )
    .then(normalize(modelInfo))
    .then((config) => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      const duration = Date() - start;
      window?.heap.track('BYO-Retrieve-Configuration-StyleID', {'load': 'success', 'duration': duration});
      return config;
    })
    .catch((error) => {
      const duration = Date() - start;
      window?.heap.track('BYO-Retrieve-Configuration-StyleID', {'load': 'error', 'status': error, 'duration': duration});
    });
};

export const fetchBySavedBuildData = (styleId, style, ss, options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const { postalCode } = options;
  const SAVED_BUILD = 'SAVED_BUILD';
  const key = `${styleId}-${ss}-${postalCode}-${SAVED_BUILD}`;
  if (cache.has(key)) {
    return Promise.resolve(cache.get(key));
  }
  const { v3ServicesPath, enableAuth, authRedirectUrl } = modelInfo;

  const url = `${v3ServicesPath}/retrieve-configuration`;
  return fetchPost(
    buildUrl(url),
    {
      style: style,
      ss: ss,
      postalCode: postalCode,
    },
    buildAuth(enableAuth, authRedirectUrl)
  )
    .then(normalize(modelInfo))
    .then((config) => {
      const key = `${config.styleId}-${config.ss}-${postalCode}-${SAVED_BUILD}`;
      cache.set(key, config);
      return config;
    });
};

export const fetchToggleOption = (styleId, ss, id, options = {}) => {
  let cache = window.__cache__;
  const modelInfo = createModelInfo();
  const {
    country,
    brand,
    language,
    carline,
    year,
    bodyStyle,
    v3ServicesPath,
    enableAuth,
    authRedirectUrl,
  } = modelInfo;
  const { postalCode } = options;

  const url = `${v3ServicesPath}/toggle-option/`;

  const start = Date();
  return fetchPost(
    buildUrl(url),
    {
      style: `${language}/${country}/${brand}/${carline}/${year}/${bodyStyle}/${styleId}`,
      rpo: id,
      ss,
      postalCode,
    },
    buildAuth(enableAuth, authRedirectUrl)
  )
    .then(normalize(modelInfo))
    .then((config) => {
      const key = `${config.styleId}-${config.ss}-${postalCode}`;
      cache.set(key, config);
      const duration = Date() - start;
      window?.heap.track('BYO-Toggle-Option', {'duration': duration});
      return config;
    })
    .catch((error) => {
      const duration = Date() - start;
      window?.heap.track('BYO-Toggle-Option', {'load': 'error', 'status': error, 'duration': duration});
    });
};
